import { useState, useEffect, useCallback } from "react";
import { Copy, Download, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Shimmer, TableShimmer } from "@/components/ui/shimmer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataTablePagination } from "@/components/ui/data-table-pagination";
import TransactionFiltersPanel, {
  getDefaultFilters,
  type FilterValues,
} from "@/components/common/TransactionFiltersPanel";
import {
  getTransactions,
  getAllTransactions,
  getTransactionTypes,
  type WalletTransactionRow,
  type TransactionFilters,
} from "@/utils/wallet-api";
import { exportWalletTransactionsToExcel } from "@/utils/export-excel";
import { useAuth } from "@/context/AuthContext";

function formatINR(paise: number): string {
  const credits = paise / 100;
  const prefix = paise >= 0 ? "+" : "-";
  return prefix + Math.abs(credits).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + " Cr";
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatType(type: string): string {
  return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function TransactionHistory() {
  const { userId, token } = useAuth();

  const [rows, setRows] = useState<WalletTransactionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [filters, setFilters] = useState<FilterValues>(getDefaultFilters);
  const [availableTypes, setAvailableTypes] = useState<string[]>([]);

  // Export state — spinner while the full (unpaginated) set is fetched.
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!userId || !token) return;
    getTransactionTypes(userId, token).then(setAvailableTypes).catch(() => {});
  }, [userId, token]);

  const fetchData = useCallback(async () => {
    if (!userId || !token) return;

    setLoading(true);
    setError("");

    const txnFilters: TransactionFilters = { userId };
    if (filters.selectedTypes.length > 0) txnFilters.types = filters.selectedTypes;
    if (filters.createdFrom) txnFilters.createdFrom = filters.createdFrom.startOf("day").toISOString();
    if (filters.createdTo) txnFilters.createdTo = filters.createdTo.endOf("day").toISOString();
    if (filters.search.trim()) txnFilters.search = filters.search.trim();

    try {
      const result = await getTransactions(txnFilters, rowsPerPage, page * rowsPerPage, token);
      setRows(result.rows);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load transactions");
    } finally {
      setLoading(false);
    }
  }, [userId, token, page, rowsPerPage, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(0); }, [filters]);

  const copyId = (id: string) => { navigator.clipboard.writeText(id); };

  /** Download every transaction matching the current filters as .xlsx.
   *  Bypasses pagination via getAllTransactions — mirrors the pattern used on
   *  CreditPage / GiftCardsOrdersPage / etc. Filters are shared with the on-
   *  screen table so the export reflects exactly what the user is looking at. */
  const handleExport = useCallback(async () => {
    if (!userId || !token || exporting) return;
    setExporting(true);
    setError("");
    try {
      const txnFilters: TransactionFilters = { userId };
      if (filters.selectedTypes.length > 0) txnFilters.types = filters.selectedTypes;
      if (filters.createdFrom) txnFilters.createdFrom = filters.createdFrom.startOf("day").toISOString();
      if (filters.createdTo) txnFilters.createdTo = filters.createdTo.endOf("day").toISOString();
      if (filters.search.trim()) txnFilters.search = filters.search.trim();

      const allRows = await getAllTransactions(txnFilters, token);
      const stamp = new Date().toISOString().slice(0, 10);
      exportWalletTransactionsToExcel(allRows, `wallet-transactions-${stamp}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Export failed");
    } finally {
      setExporting(false);
    }
  }, [userId, token, exporting, filters]);

  return (
    <Shimmer loading={loading} fallback={<TableShimmer />}>
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-6">
          <div>
            <h3 className="text-base font-semibold">Transaction History</h3>
            <p className="text-sm text-muted-foreground">
              {total.toLocaleString()} transactions total
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={exporting || total === 0}
            className="gap-1.5"
            title="Download all transactions matching current filters as Excel"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {exporting ? "Exporting..." : "Export Excel"}
          </Button>
        </div>

        <TransactionFiltersPanel
          filters={filters}
          onChange={setFilters}
          availableTypes={availableTypes}
        />

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold">Transaction ID</TableHead>
                <TableHead className="font-semibold">Purpose</TableHead>
                <TableHead className="font-semibold">Type</TableHead>
                <TableHead className="font-semibold text-right">Amount</TableHead>
                <TableHead className="font-semibold">Created At</TableHead>
                <TableHead className="font-semibold">Updated At</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                    No transactions found
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((txn) => (
                  <TableRow key={txn.id}>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <span className="font-mono text-xs">{txn.id.slice(0, 8)}...</span>
                        <Button variant="ghost" size="icon-xs" onClick={() => copyId(txn.id)}>
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm max-w-[260px] truncate block" title={txn.purpose}>
                        {txn.purpose}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={txn.amount >= 0 ? "border-green-500 text-green-600 dark:text-green-400" : ""}>
                        {formatType(txn.type)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`text-sm font-semibold ${txn.amount >= 0 ? "text-green-600 dark:text-green-400" : "text-red-500 dark:text-red-400"}`}>
                        {formatINR(txn.amount)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">{formatDate(txn.created_at)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">{formatDate(txn.updated_at)}</span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <DataTablePagination
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={(rpp) => { setRowsPerPage(rpp); setPage(0); }}
        />
      </div>
      </Card>
    </Shimmer>
  );
}
